import * as THREE from 'three'
import { controllerRay, rotateTrackedPosition } from './xrInput'
import type { BlockLookup, Point } from './xrTeleportGeometry'

export function postureEyeHeight(posture: 'seated' | 'standing', floorSpace: boolean, baselineY: number) {
  return posture === 'standing' && floorSpace ? Math.max(1.35, Math.min(1.8, baselineY)) : 1.62
}

/** Keep the tracked eyes, including both stereo eyes, outside blocks and unloaded space. */
function eyeClear(point: Point, blockAt: BlockLookup) {
  const radius = 0.12
  for (let x = Math.floor(point.x - radius); x <= Math.floor(point.x + radius); x++) for (let y = Math.floor(point.y - radius); y <= Math.floor(point.y + radius); y++) for (let z = Math.floor(point.z - radius); z <= Math.floor(point.z + radius); z++) {
    const block = blockAt(x, y, z)
    if (!block) return false
    for (const box of block.shapes) {
      if (x + box[0] < point.x + radius && x + box[3] > point.x - radius && y + box[1] < point.y + radius && y + box[4] > point.y - radius && z + box[2] < point.z + radius && z + box[5] > point.z - radius) return false
    }
  }
  return true
}

/** The first pose defines a baseline; later physical head movement is preserved, not canceled. */
export function calibratedEye(head: Point, baseline: Point, yaw: number, feet: Point, eyeHeight: number, blockAt: BlockLookup): Point {
  const lean = rotateTrackedPosition({ x: head.x - baseline.x, y: head.y - baseline.y, z: head.z - baseline.z }, yaw)
  const horizontal = Math.hypot(lean.x, lean.z)
  if (horizontal > 0.45) { lean.x *= 0.45 / horizontal; lean.z *= 0.45 / horizontal }
  lean.y = Math.max(0.35 - eyeHeight, Math.min(0.18, lean.y))
  const base = { x: feet.x, y: feet.y + eyeHeight, z: feet.z }
  // A standing calibration plus Minecraft crouch can place the initial eye sphere
  // inside a low slab ceiling. Find the nearest clear lower baseline first.
  if (!eyeClear(base, blockAt)) {
    for (let drop = 0.025; base.y - drop >= feet.y + 0.35; drop += 0.025) {
      const candidate = { ...base, y: base.y - drop }
      if (eyeClear(candidate, blockAt)) { base.y = candidate.y; break }
    }
  }
  lean.y = Math.max(feet.y + 0.35 - base.y, lean.y)
  let safe = base
  const steps = Math.max(1, Math.ceil(Math.hypot(lean.x, lean.y, lean.z) / 0.025))
  for (let step = 1; step <= steps; step++) {
    const next = { x: base.x + lean.x * step / steps, y: base.y + lean.y * step / steps, z: base.z + lean.z * step / steps }
    if (!eyeClear(next, blockAt)) break
    safe = next
  }
  return safe
}

/** Scene origin follows the calibrated world eye, so hand transforms remain eye-relative. */
export function trackedScenePose(transform: { position: Point; orientation: { x: number; y: number; z: number; w: number } }, head: Point, yaw: number) {
  const ray = controllerRay(transform, head, yaw)
  return {
    position: new THREE.Vector3().copy(ray.origin),
    quaternion: new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(0, 1, 0), yaw).multiply(new THREE.Quaternion(transform.orientation.x, transform.orientation.y, transform.orientation.z, transform.orientation.w)),
  }
}
